/**
 * `/internal/ops-users/*` contract types (ADR-004). Epic #109 P3.18.
 *
 * Ops-admin surface for managing ops user accounts: list, create,
 * update roles/Feishu binding, disable, enable, reset password, and
 * revoke live sessions.
 */

import type { BrandId } from './brands.ts';

export const ROLE_OPS_SUPER_ADMIN = 'ops-super-admin';
export const ROLE_OPS_ADMIN = 'ops-admin';
export const ROLE_OPS_SUPPORT = 'ops-support';
export const ROLE_OPS_SALES_LEAD = 'ops-sales-lead';
export const ROLE_OPS_CUSTOMER_MANAGER = 'ops-customer-manager';
export const ROLE_OPS_FINANCE_MANAGER = 'ops-finance-manager';
export const ROLE_OPS_SUPPLY_DEMAND_MANAGER = 'ops-supply-demand-manager';
export const ROLE_OPS_RUNTIME_OPERATOR = 'ops-runtime-operator';

export const OPS_ROLE_DISPLAY_ORDER = [
  ROLE_OPS_SUPER_ADMIN,
  ROLE_OPS_ADMIN,
  ROLE_OPS_SUPPORT,
  ROLE_OPS_SALES_LEAD,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_FINANCE_MANAGER,
  ROLE_OPS_SUPPLY_DEMAND_MANAGER,
  ROLE_OPS_RUNTIME_OPERATOR,
] as const;
export type OpsPermissionRole = (typeof OPS_ROLE_DISPLAY_ORDER)[number];

/**
 * Roles that ordinary ops-admin user-management flows may grant.
 *
 * `ops-super-admin` is intentionally not assignable through the generic
 * ops-users API/UI. It is a bootstrap/break-glass role whose assignment
 * should stay out-of-band until a dedicated elevated approval flow exists.
 */
export const ASSIGNABLE_OPS_ROLES = [
  ROLE_OPS_ADMIN,
  ROLE_OPS_SUPPORT,
  ROLE_OPS_SALES_LEAD,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_FINANCE_MANAGER,
  ROLE_OPS_SUPPLY_DEMAND_MANAGER,
  ROLE_OPS_RUNTIME_OPERATOR,
] as const;
export type AssignableOpsRole = (typeof ASSIGNABLE_OPS_ROLES)[number];

export function expandOpsRoles(rawRoles: readonly string[]): string[] {
  const roleSet = new Set(rawRoles.filter((role) => role.length > 0));
  if (roleSet.has(ROLE_OPS_SUPER_ADMIN)) {
    roleSet.add(ROLE_OPS_ADMIN);
    roleSet.add(ROLE_OPS_SUPPORT);
    roleSet.add(ROLE_OPS_SALES_LEAD);
    roleSet.add(ROLE_OPS_CUSTOMER_MANAGER);
    roleSet.add(ROLE_OPS_FINANCE_MANAGER);
    roleSet.add(ROLE_OPS_SUPPLY_DEMAND_MANAGER);
    roleSet.add(ROLE_OPS_RUNTIME_OPERATOR);
  }
  if (roleSet.has(ROLE_OPS_ADMIN)) {
    roleSet.add(ROLE_OPS_SUPPORT);
  }
  // Epic #1030 (sales system): a sales lead can do everything a customer
  // manager can, plus approve/configure — mirror the admin ⊃ support edge.
  if (roleSet.has(ROLE_OPS_SALES_LEAD)) {
    roleSet.add(ROLE_OPS_CUSTOMER_MANAGER);
  }

  const expanded: string[] = [];
  for (const role of OPS_ROLE_DISPLAY_ORDER) {
    if (roleSet.has(role)) {
      expanded.push(role);
      roleSet.delete(role);
    }
  }
  for (const role of rawRoles) {
    if (roleSet.has(role)) {
      expanded.push(role);
      roleSet.delete(role);
    }
  }
  return expanded;
}

export function hasAnyOpsRole(
  rawRoles: readonly string[],
  requiredRoles: readonly string[],
): boolean {
  if (requiredRoles.length === 0) {
    return true;
  }
  const effective = new Set(expandOpsRoles(rawRoles));
  return requiredRoles.some((role) => effective.has(role));
}

export interface OpsUserGithubIdentity {
  github_user_id: string;
  github_login: string;
  github_avatar_url: string | null;
}

export interface OpsUserFeishuIdentity {
  feishu_tenant_key: string;
  feishu_open_id: string;
  feishu_union_id: string | null;
  feishu_user_id: string | null;
  feishu_name: string | null;
  feishu_avatar_url: string | null;
  feishu_enterprise_email: string | null;
}

export interface OpsUserRecord {
  id: string;
  email: string;
  /**
   * Operator-facing identity label. Computed by platform-api from the
   * freshest safe profile fields; not editable as an independent column.
   */
  display_name: string;
  created_at: string;
  disabled_at: string | null;
  github_user_id: string | null;
  github_login: string | null;
  github_avatar_url: string | null;
  github_linked_at: string | null;
  github_last_login_at: string | null;
  feishu_tenant_key: string | null;
  feishu_open_id: string | null;
  feishu_union_id: string | null;
  feishu_user_id: string | null;
  feishu_name: string | null;
  feishu_avatar_url: string | null;
  feishu_enterprise_email: string | null;
  feishu_linked_at: string | null;
  feishu_last_login_at: string | null;
  session_revoked_before: string | null;
  /**
   * Whitelist of roles assigned to this user (JSON array in the DB).
   * Treated as opaque strings; role semantics live in ops-api
   * `requireRoles`.
   */
  roles: string[];
  /**
   * `true` iff an ops_password_credentials row exists for this user.
   * `false` when no password has been seeded — either the account was
   * "reset" (credential deleted) or it only ever signed in via Feishu SSO,
   * an independent login path that never seeds a password. Password-less is
   * therefore not on its own a login blocker: a Feishu-bound account can
   * still authenticate. An account is only locked out when it has neither a
   * password nor a Feishu binding.
   */
  has_password: boolean;
}

export interface ListOpsUsersResponse {
  users: OpsUserRecord[];
}

export interface CreateOpsUserRequest {
  email: string;
  roles: string[];
  feishu_identity?: OpsUserFeishuIdentity | null;
  /** Legacy compatibility: GitHub login is no longer the primary ops SSO. */
  github_identity?: OpsUserGithubIdentity | null;
}

export interface CreateOpsUserResponse {
  user: OpsUserRecord;
}

export interface UpdateOpsUserRequest {
  email?: string;
  roles?: string[];
  /** Omit to keep existing binding, object to bind/update, null to unbind. */
  feishu_identity?: OpsUserFeishuIdentity | null;
  /** Legacy compatibility: GitHub login is no longer the primary ops SSO. */
  github_identity?: OpsUserGithubIdentity | null;
}

export interface UpdateOpsUserResponse {
  user: OpsUserRecord;
}

/** `POST /internal/ops-users/:id/disable` has an empty body. */
export interface DisableOpsUserResponse {
  user: OpsUserRecord;
}

/** `POST /internal/ops-users/:id/enable` has an empty body. */
export interface EnableOpsUserResponse {
  user: OpsUserRecord;
}

/**
 * `POST /internal/ops-users/:id/reset-password` — deletes the
 * user's `ops_password_credentials` row. Forces an admin re-seed via
 * a fresh INSERT (out-of-band, typically via wrangler / the setup
 * script); there is no user-facing self-service reset flow in phase 1.
 * Also invalidates all live ops sessions for the user so the access
 * cookie can't outlive the credential.
 */
export interface ResetOpsUserPasswordResponse {
  user: OpsUserRecord;
  /** Count of `ops_sessions` rows deleted by the reset. */
  sessions_invalidated: number;
}

/** `POST /internal/ops-users/:id/revoke-sessions` has an empty body. */
export interface RevokeOpsUserSessionsResponse {
  user: OpsUserRecord;
  /** Count of `ops_sessions` rows deleted by the revocation. */
  sessions_invalidated: number;
}

// ─── sales profiles (epic #1030 E1-S1, #1037) ────────────────────────────

/**
 * One row per sales user in `sales_profiles`: exactly one brand per
 * ops user. Brand is validated against the shared brand registry
 * (`BRAND_IDS` in `brands.ts`) at the platform-api boundary.
 */
export interface SalesProfileRecord {
  ops_user_id: string;
  brand: BrandId;
  created_at: string;
  updated_at: string;
}

/** `GET /internal/ops-users/:id/sales-profile` — `profile` is null when unset. */
export interface GetSalesProfileResponse {
  profile: SalesProfileRecord | null;
}

/**
 * `PUT /internal/ops-users/:id/sales-profile` — insert-or-update the
 * user's brand. Rejected with `INVALID_PAYLOAD` when `brand` is outside
 * the brand registry, and with `SALES_PROFILE_HAS_ACTIVE_CUSTOMERS`
 * when the brand would CHANGE while the user still owns active
 * customers (fail-closed; transfer the book first).
 */
export interface SetSalesProfileRequest {
  brand: string;
}

export interface SetSalesProfileResponse {
  profile: SalesProfileRecord;
}

/**
 * `DELETE /internal/ops-users/:id/sales-profile` — rejected with
 * `SALES_PROFILE_HAS_ACTIVE_CUSTOMERS` while the user owns active
 * (`revoked_at IS NULL`) `customer_manager_assignments` rows.
 */
export interface DeleteSalesProfileResponse {
  deleted: true;
  ops_user_id: string;
}
